import React, { useCallback, useEffect, useState } from 'react';
import './RedeemCouponSheet.css';

const e = React.createElement;

// Long enough for the slide-down to finish before the sheet unmounts. Kept in
// step with the .rcs-sheet.closing animation.
const CLOSE_MS = 200;

/**
 * The plumbing every wallet bottom sheet needs, in one place: the closing
 * animation the sheet has to outlive before it unmounts, Escape to dismiss,
 * and the page behind held still while the sheet is up.
 *
 * `blocked` is for a sheet that must not be dismissed at a particular moment
 * -- mid-request, say, where closing would leave the customer not knowing
 * whether what they started landed.
 *
 * Hands back a close request rather than calling `onClose` itself, because
 * the sheet's own buttons need the animated close too.
 */
export const useBottomSheet = ({ open, onClose, blocked = false }) => {
  const [closing, setClosing] = useState(false);

  // Every open starts from rest: a sheet dismissed mid-animation must not
  // come back up already playing its exit.
  useEffect(() => {
    if (open) setClosing(false);
  }, [open]);

  const requestClose = useCallback(() => {
    if (blocked) return;
    setClosing(true);
    setTimeout(() => {
      setClosing(false);
      onClose();
    }, CLOSE_MS);
  }, [blocked, onClose]);

  // Escape to dismiss, and the page behind must not scroll under the sheet.
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (evt) => {
      if (evt.key === 'Escape') requestClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, requestClose]);

  return { closing, requestClose };
};

/**
 * Scrim, sheet and grabber with the dialog wiring, around whatever panel the
 * sheet wants to show. A click that lands on the scrim itself -- not on the
 * sheet in front of it -- dismisses.
 */
export const bottomSheet = ({ closing, requestClose, labelledBy, panel }) =>
  e(
    'div',
    {
      className: `rcs-scrim${closing ? ' closing' : ''}`,
      onClick: (evt) => {
        if (evt.target === evt.currentTarget) requestClose();
      },
    },
    e(
      'div',
      {
        className: `rcs-sheet${closing ? ' closing' : ''}`,
        role: 'dialog',
        'aria-modal': 'true',
        'aria-labelledby': labelledBy,
      },
      e('span', { className: 'rcs-grabber' }),
      panel
    )
  );
